import { holdsRole } from '../utils/heldRoles';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { calculateDaysLate, getDaysLateLabel } from '../utils/daysLateCalculator';
import { isTaxProduct, isSearchProduct, getCallbackSheetLabel } from '../utils/orderHelpers';
import { getRoleDashboardPath } from '../utils/roleDashboard';
import {
  formatStreetAddress, formatCity, formatState, formatCounty, EMPTY_PLACEHOLDER,
} from '../utils/textFormat';
import { normalizeBorrowerName } from '../utils/nameNormalizer';
import MailAwayStatusBlock from './MailAwayStatusBlock';
import MultiJurisdictionMailAway from './MultiJurisdictionMailAway';
import { MlsMailAwaySummaryRows, mlsMailAwaySummary, isMlsMailAwayRow } from './mls/MlsMailAwaySummaryRows';
import { MailAwayRecoveryCard } from './orders/ReturnToMailAwayDialog';
import ClarificationStatusBlock from './ClarificationStatusBlock';
import FaxDeliverySection from './orders/FaxDeliverySection';
import TaxEmailRequestsSection from './orders/TaxEmailRequestsSection';
import TypingOrderSection from './typing/TypingOrderSection';
import ResearchLinksCard from './search/ResearchLinksCard';
import OrderReassignmentDialog from './orders/OrderReassignmentDialog';
import { CopyIconButton } from './ui/copy-field';
import axios from 'axios';
import { toast } from 'sonner';
import { formatInTimeZone } from 'date-fns-tz';
import { formatDateShortET, formatDateOnly } from '../utils/formatters';
import { ContractCodeChip, productDisplayText } from './orders/ProductWithCode';

import { RestoreOrderModal } from './orders/RestoreOrderModal';
import { FileText, Building2, Briefcase, MapPin, UserRound, Box, CalendarDays, CalendarClock, Clock, X as XIcon, ClipboardList, ArrowRight, UserPlus, UserMinus, RefreshCw, Mail, MailCheck, HelpCircle, StickyNote, ShieldAlert } from 'lucide-react';
const API = process.env.REACT_APP_BACKEND_URL;
const ACCOUNT_TZ = 'America/New_York';

/* ORDER-SUMMARY-PREMIUM-001 (Oct 2026) — presentational helpers (UI only). */
const OS_T = 'transition-[box-shadow,border-color,background-color,transform,color] duration-200 ease-out';
const SCard = ({ children, className = '', testId }) => (
  <div className={`bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] animate-in fade-in-0 slide-in-from-bottom-1 duration-200 ${OS_T} hover:shadow-[0_6px_20px_-10px_rgba(15,23,42,0.25)] hover:border-slate-300 ${className}`} data-testid={testId}>
    {children}
  </div>
);
const SIcon = ({ icon: Icon, tone = 'blue' }) => {
  const tones = { blue: 'bg-blue-50 text-[#2563eb]', violet: 'bg-violet-50 text-violet-600', emerald: 'bg-emerald-50 text-emerald-600', rose: 'bg-rose-50 text-rose-500', amber: 'bg-amber-50 text-amber-600', slate: 'bg-slate-100 text-slate-500' };
  return <span className={`h-10 w-10 rounded-xl grid place-items-center shrink-0 ${tones[tone] || tones.blue}`}><Icon className="w-[18px] h-[18px]" aria-hidden="true" /></span>;
};
const SLabel = ({ children }) => <span className="block text-[10.5px] font-semibold uppercase tracking-[0.08em] text-slate-500 leading-tight mb-0.5">{children}</span>;
const SValue = ({ children, size = 'md', className = '', ...rest }) => (
  <span {...rest} className={`${size === 'lg' ? 'text-[18px] font-bold tracking-tight' : 'text-[14px] font-semibold'} text-slate-900 break-words min-w-0 leading-snug ${className}`}>{children}</span>
);

/**
 * Recent Activity Section Component
 * Shows the last 5 activities for an order
 *
 * Exported so the Summary tab page (`SummaryTab.jsx`) can reuse the
 * exact same activity strip the popup shows — keeps both surfaces
 * visually identical without duplication.
 */
export const RecentActivitySection = ({ orderId, refreshKey = 0, onViewAll = null }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivities = async () => {
      if (!orderId) return;
      
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        // Use the correct API endpoint with proper fallback
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const baseUrl = API_URL ? `${API_URL}/api` : '/api';
        const response = await axios.get(
          `${baseUrl}/orders/${orderId}/activity?limit=5`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setActivities(response.data.events || []);
      } catch (error) {
        console.error('Failed to fetch activities:', error);
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchActivities();
  }, [orderId, refreshKey]);
  
  const getActivityIcon = (type) => {
    if (type?.includes('mailaway.sent') || type?.includes('mailaway.email_sent')) {
      return '📧';
    } else if (type?.includes('mailaway.response_received')) {
      return '✅';
    } else if (type?.includes('status.rollup_change') || type?.includes('status.change')) {
      return '🔄';
    } else if (type?.includes('mailaway')) {
      return '📮';
    } else if (type?.includes('clarification')) {
      return '❓';
    } else if (type?.includes('assigned')) {
      return '👤';
    }
    return '📝';
  };
  
  // Helper to ensure timestamp is treated as UTC (backend returns timestamps without 'Z')
  const ensureUtcTimestamp = (timestamp) => {
    if (!timestamp) return null;
    if (timestamp instanceof Date) return timestamp.toISOString();
    const str = String(timestamp);
    if (!str.endsWith('Z') && !str.match(/[+-]\d{2}:\d{2}$/)) {
      return str + 'Z';
    }
    return str;
  };

  // V1 REQUIREMENT: All timestamps displayed in America/New_York (ET)
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      // Show full date and time in Eastern Time
      const utcTimestamp = ensureUtcTimestamp(dateStr);
      return formatInTimeZone(utcTimestamp, ACCOUNT_TZ, 'MM/dd/yyyy h:mm a') + ' ET';
    } catch (error) {
      return 'Invalid date';
    }
  };
  
  const ActivityIcon = ({ type }) => {
    const t = type || '';
    let Icon = StickyNote, cls = 'text-amber-500';
    if (t.includes('duplicate')) { Icon = ShieldAlert; cls = 'text-amber-600'; }
    else if (t.includes('unassigned')) { Icon = UserMinus; cls = 'text-rose-500'; }
    else if (t.includes('assigned')) { Icon = UserPlus; cls = 'text-blue-600'; }
    else if (t.includes('mailaway.response_received')) { Icon = MailCheck; cls = 'text-emerald-600'; }
    else if (t.includes('mailaway')) { Icon = Mail; cls = 'text-violet-600'; }
    else if (t.includes('status')) { Icon = RefreshCw; cls = 'text-sky-600'; }
    else if (t.includes('clarification')) { Icon = HelpCircle; cls = 'text-amber-600'; }
    return <Icon className={`w-3.5 h-3.5 ${cls}`} aria-hidden="true" />;
  };
  const dotTone = (type) => {
    const t = type || '';
    if (t.includes('duplicate')) return 'bg-amber-400';
    if (t.includes('unassigned')) return 'bg-rose-400';
    if (t.includes('assigned')) return 'bg-blue-500';
    if (t.includes('mailaway')) return 'bg-violet-400';
    if (t.includes('status')) return 'bg-sky-400';
    return 'bg-slate-300';
  };

  if (loading) {
    return (
      <div className="mt-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-400 flex items-center gap-2" data-testid="order-summary-activity-loading">
        <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        Loading activity...
      </div>
    );
  }

  if (activities.length === 0) {
    return null;
  }

  // Backend caps at 5 events (`limit=5`); all are rendered — nothing is fabricated or hidden.
  return (
    <div className="mt-1 rounded-xl border border-slate-200 bg-white overflow-hidden shadow-[0_1px_2px_rgba(15,23,42,0.04)]" data-testid="order-summary-activity">
      <div className="flex items-center justify-between px-4 py-1.5 bg-gradient-to-r from-blue-50/80 to-indigo-50/40 border-b border-slate-100">
        <span className="inline-flex items-center gap-2 text-[14px] font-bold text-slate-900">
          <span className="h-7 w-7 rounded-lg bg-white text-[#2563eb] grid place-items-center ring-1 ring-blue-100"><ClipboardList className="w-4 h-4" aria-hidden="true" /></span>
          Recent Activity
        </span>
        {onViewAll && (
          <button type="button" onClick={onViewAll} data-testid="order-summary-view-full-activity"
            className={`inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#2563eb] ${OS_T} hover:text-[#1d4ed8] hover:translate-x-0.5`}>
            View Full Activity <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </div>
      <ol className="relative px-4 py-1 before:absolute before:left-[23px] before:top-2 before:bottom-2 before:w-px before:bg-slate-200">
        {activities.map((activity, idx) => {
          const d = activity.details || {};
          const reasonText =
            (typeof d.reason === 'string' && d.reason.trim()) ||
            (typeof d.response_details === 'string' && d.response_details.trim()) ||
            (typeof d.notes === 'string' && d.notes.trim()) ||
            '';
          const reasonLabel = d.response_details
            ? 'Response'
            : activity.type === 'status.clarification_reopen'
              ? 'Re-Open Reason'
              : 'Reason';
          const actor = activity.actor?.name || activity.actor_name || activity.user_name || '';
          return (
            <li
              key={activity.id || idx}
              className={`relative flex items-start gap-3 pl-5 pr-2 py-[3px] rounded-lg ${OS_T} hover:bg-slate-50`}
              data-testid="order-summary-activity-row"
            >
              <span className={`absolute left-[5px] top-[9px] h-2 w-2 rounded-full ring-4 ring-white ${dotTone(activity.type)}`} aria-hidden="true" />
              <span className="mt-0.5 shrink-0"><ActivityIcon type={activity.type} /></span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                  <span className="text-[12.5px] text-slate-800 break-words leading-snug">{activity.summary}</span>
                  <span className="text-[11.5px] text-slate-400 whitespace-nowrap">
                    {actor ? `${actor} · ` : ''}{formatTime(activity.created_at)}
                  </span>
                </div>
                {reasonText && (
                  <div className="text-[11.5px] text-slate-500 break-words whitespace-pre-wrap leading-snug" data-testid="order-summary-activity-reason">
                    <span className="font-semibold text-slate-500">{reasonLabel}:</span>{' '}
                    <span className="text-slate-600">{reasonText}</span>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

/**
 * Premium Order Summary Popup
 * 
 * Displays order details for Tax Search and Update Tax Search orders
 * Production-grade SaaS UI with card-based layout
 * 
 * UPDATED: Now shows ALL fields for all roles (Admin, Team Leader, Processor)
 * with support for both snake_case and camelCase field names
 * ENHANCED: Quick Priority Update for Admin/Team Leader
 * ENHANCED: Request Edit button for Order Desk users viewing assigned orders
 */
// RL-HIDE-TAX-001: tax roles (current + legacy spellings) never see Research Links in Order Summary.
const RESEARCH_LINKS_HIDDEN_ROLES = ['tax_processor', 'processor', 'tax_search_team_leader', 'tax_team_leader'];

const OrderSummaryPopup = ({ isOpen, onClose, orderData: orderDataProp, currentUser, onUpdate, showRequestEditButton = false, onRequestEdit, showEditOrderButton = false, onEditOrder }) => {
  const navigate = useNavigate();

  // ---------------------------------------------------------------
  // Order hydration (May 8, 2026 — TF-20260503-0684 clarification reason
  // regression fix)
  //
  // The orders-list endpoints (/api/orders, /api/orders-v2) strip heavy
  // fields via `_LIST_PROJECTION_EXCLUDE` for performance. The list rows
  // arrive without `status_notes`, `clarification_responses`, `files`,
  // etc. — but this popup is fed those rows directly and the
  // `ClarificationStatusBlock` reads exactly those stripped fields to
  // render the amber "Clarification Requested" reason box. Result:
  // blank reason area for every clarification order.
  //
  // Fix (Option A — hydrate the modal): when the popup opens, fetch the
  // full order document via `GET /api/orders/{id}/details` (no
  // projection) and use the hydrated copy everywhere downstream. The
  // list payload stays lean; the full doc is only loaded when the user
  // actually opens a row. We hydrate by id (not order_number) and
  // gracefully fall back to the raw row if the fetch fails so the
  // popup never goes empty.
  // ---------------------------------------------------------------
  const [hydratedOrder, setHydratedOrder] = useState(null);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);

  // MAILAWAY-RECOVERY-002: reusable so in-popup actions (e.g. Return to Mail-Away) can
  // reconcile the authoritative order without close/reopen or a page reload.
  const rehydrate = useCallback(async (isCancelled = () => false) => {
    if (!orderDataProp?.id) return;
    try {
      const token = localStorage.getItem('token');
      const baseUrl = API ? `${API}/api` : '/api';
      const resp = await axios.get(
        `${baseUrl}/orders/${orderDataProp.id}/details`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (isCancelled()) return;
      const full = resp?.data?.order || null;
      if (full) {
        // Merge: full doc wins, but preserve any list-only enrichments
        // (e.g. `_resolved_assignee_name`) the table layer added.
        setHydratedOrder((prev) => ({ ...orderDataProp, ...(prev || {}), ...full }));
      }
    } catch (err) {
      // Non-fatal — popup still renders from list-row data.
      console.warn('OrderSummaryPopup: hydration failed, using list row', err);
    }
  }, [orderDataProp]);

  useEffect(() => {
    if (!isOpen || !orderDataProp?.id) {
      setHydratedOrder(null);
      return;
    }
    let cancelled = false;
    rehydrate(() => cancelled);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, orderDataProp?.id]);

  // Hydrated copy takes precedence; falls back to the raw list row
  // until hydration completes (or if it fails). Every reference below
  // reads from this single resolved object.
  const orderData = hydratedOrder || orderDataProp;

  // Priority state for quick update
  const [priority, setPriority] = useState(orderData?.priority || 'normal');
  const [priorityNotes, setPriorityNotes] = useState(orderData?.priority_notes || '');
  const [priorityError, setPriorityError] = useState('');
  const [isSavingPriority, setIsSavingPriority] = useState(false);
  const [showPriorityPanel, setShowPriorityPanel] = useState(false);
  
  // Cancel Order state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState('');
  
  // Restore Order state
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  
  // Reopen for Edit state (for completed orders)
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [isReopening, setIsReopening] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenError, setReopenError] = useState('');
  
  // Reassign state
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  
  // Sync priority state when orderData changes
  useEffect(() => {
    if (orderData) {
      setPriority(orderData.priority || 'normal');
      setPriorityNotes(orderData.priority_notes || '');
    }
  }, [orderData?.priority, orderData?.priority_notes]);
  
  // Check if user can edit priority
  const canEditPriority = currentUser?.role === 'admin' || 
                          currentUser?.role === 'tax_search_team_leader' || 
                          currentUser?.role === 'title_search_team_leader' ||
                          currentUser?.role === 'team_leader';
  
  // Check if user can cancel/restore orders (Admin and Team Leaders only)
  const canManageOrderStatus = currentUser?.role === 'admin' || 
                          currentUser?.role === 'tax_search_team_leader' || 
                          currentUser?.role === 'title_search_team_leader' ||
                          currentUser?.role === 'team_leader';
  
  // Check if user can access fulfillment/processor tools (NOT Order Desk)
  // OD-DUAL-ROLE-001: an Order Desk user who HOLDS the additional Search Processor role is a processor too.
  const canAccessFulfillment = currentUser?.role !== 'order_desk' || (holdsRole(currentUser, 'search_processor') && orderData?.assignee_id === currentUser?.id);
  
  // Calculate Days Late
  const daysLateInfo = orderData ? calculateDaysLate(orderData) : null;
  
  // Check order status for action visibility
  const orderStatus = orderData?.status?.toLowerCase();
  // ORDER-RESTORE-STATE-001 — completed orders (complete/completed) stay cancellable; cancel preserves workflow state
  const isOrderCancellable = orderStatus !== 'cancelled';
  const isOrderRestorable = orderStatus === 'cancelled';

  if (!isOpen || !orderData) return null;

  // Helper to get value from either snake_case or camelCase field names
  const getValue = (snakeCase, camelCase, defaultVal = 'N/A') => {
    return orderData[snakeCase] || orderData[camelCase] || defaultVal;
  };
  
  // Quick Priority Save Handler
  const handleQuickPrioritySave = async (newPriority = priority) => {
    // Validate justification for Rush/Urgent
    if ((newPriority === 'rush' || newPriority === 'urgent') && !priorityNotes.trim()) {
      setPriorityError('Justification is required for Rush/Urgent priority');
      setShowPriorityPanel(true);
      return;
    }
    
    setPriorityError('');
    setIsSavingPriority(true);
    
    try {
      await axios.put(`${API}/api/orders/${orderData.id}`, {
        priority: newPriority,
        priority_notes: priorityNotes
      });
      
      // Update local state first
      setPriority(newPriority);
      setShowPriorityPanel(false);
      
      // Show success toast
      toast.success(`Priority updated to ${newPriority.toUpperCase()}`, {
        icon: newPriority === 'urgent' ? '🔴' : newPriority === 'rush' ? '🔵' : '🟡',
        duration: 3000
      });
      
      // Notify parent to refresh order data (wrapped in try-catch to prevent errors)
      try {
        if (onUpdate && typeof onUpdate === 'function') {
          onUpdate({ ...orderData, priority: newPriority, priority_notes: priorityNotes });
        }
      } catch (callbackError) {
        console.warn('onUpdate callback error (non-critical):', callbackError);
      }
    } catch (error) {
      console.error('Error updating priority:', error);
      // Only show error toast if it's actually an API error
      if (error.response) {
        toast.error(error.response?.data?.detail || 'Failed to update priority');
      } else if (error.request) {
        toast.error('Network error - please check your connection');
      } else {
        toast.error('Failed to update priority');
      }
    } finally {
      setIsSavingPriority(false);
    }
  };
  
  // Quick action buttons for Rush/Urgent
  const handleMarkAsRush = () => {
    setPriority('rush');
    setShowPriorityPanel(true);
  };
  
  const handleMarkAsUrgent = () => {
    setPriority('urgent');
    setShowPriorityPanel(true);
  };

  // Cancel Order handler
  const handleCancelOrder = async () => {
    // Validate reason (minimum 10 characters as required by backend)
    if (!cancelReason.trim() || cancelReason.trim().length < 10) {
      setCancelError('Please provide a reason (at least 10 characters)');
      return;
    }
    
    setCancelError('');
    setIsCancelling(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/api/orders/${orderData.id}/status`,
        { status: 'cancelled', reason: cancelReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Order cancelled successfully', {
        icon: '🚫',
        duration: 3000
      });
      
      setShowCancelModal(false);
      setCancelReason('');
      
      // Notify parent to refresh
      try {
        if (onUpdate && typeof onUpdate === 'function') {
          onUpdate({ ...orderData, status: 'cancelled' });
        }
      } catch (callbackError) {
        console.warn('onUpdate callback error (non-critical):', callbackError);
      }
      
      // Close the popup after a short delay
      setTimeout(() => {
        onClose();
      }, 500);
      
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setIsCancelling(false);
    }
  };
  
  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
    setCancelReason('');
    setCancelError('');
  };

  // ORDER-RESTORE-STATE-001 — restore handled by RestoreOrderModal (stage-aware)
  const handleRestored = (updatedOrder) => {
    setShowRestoreModal(false);
    try { if (onUpdate && typeof onUpdate === 'function') onUpdate({ ...orderData, ...(updatedOrder || {}) }); }
    catch (callbackError) { console.warn('onUpdate callback error (non-critical):', callbackError); }
    setTimeout(() => onClose(), 500);
  };

  // Reopen for Edit handler (for completed orders)
  const handleReopenOrder = async () => {
    if (!reopenReason.trim() || reopenReason.trim().length < 10) {
      setReopenError('Reopen reason must be at least 10 characters');
      return;
    }
    
    setIsReopening(true);
    setReopenError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/orders/${orderData.id}/reopen`,
        { reopen_reason: reopenReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Order reopened successfully! Status set to Open, unassigned, and ready for re-assignment. Redirecting to dashboard...', {
        icon: '✅',
        duration: 3000
      });
      
      setShowReopenModal(false);
      setReopenReason('');
      
      // Notify parent to refresh
      try {
        if (onUpdate && typeof onUpdate === 'function') {
          onUpdate(response.data.order);
        }
      } catch (callbackError) {
        console.warn('onUpdate callback error (non-critical):', callbackError);
      }
      
      // Close popup and redirect to dashboard after 1.5 seconds
      setTimeout(() => {
        onClose();
        // Feb 27, 2026 — role-aware dashboard
        navigate(getRoleDashboardPath(currentUser), { replace: true });
      }, 1500);
      
    } catch (error) {
      console.error('Error reopening order:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to reopen order';
      setReopenError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsReopening(false);
    }
  };

  const handleOpenFulfillment = () => {
    onClose();
    // Navigate directly to Fulfillment tab
    navigate(`/orders/${orderData.id}/fulfillment`);
  };

  // V1 REQUIREMENT: Use ET timezone for all timestamps
  const formatDate = formatDateShortET;

  // Calculate days elapsed since order created (calendar-day based)
  const calculateDaysElapsed = () => {
    const createdDate = orderData.created_at || orderData.createdAt || orderData.created_date || orderData.orderedOn;
    if (!createdDate) return null;
    try {
      // Parse the created date - handle both ISO string and Date object
      const created = new Date(createdDate);
      if (isNaN(created.getTime())) return null;
      
      // Get current time
      const now = new Date();
      
      // Use calendar day comparison for more intuitive results
      // An order created yesterday should show 1 day elapsed, not 0
      const createdDay = new Date(created.getFullYear(), created.getMonth(), created.getDate());
      const todayDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      // Calculate difference in calendar days
      const diffTime = todayDay.getTime() - createdDay.getTime();
      const diffDays = Math.max(0, Math.round(diffTime / (1000 * 60 * 60 * 24)));
      
      return diffDays;
    } catch (e) {
      console.error('Error calculating days elapsed:', e);
      return null;
    }
  };

  // Calculate days remaining until due date
  const calculateDaysRemaining = () => {
    const dueDate = orderData.due_date || orderData.dueDate || orderData.dueBy;
    if (!dueDate) return null;
    try {
      const due = new Date(dueDate);
      const now = new Date();
      const diffTime = due - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };

  const daysElapsed = calculateDaysElapsed();
  const daysRemaining = calculateDaysRemaining();

  // Status badge styling
  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case 'complete':
      case 'completed':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'in_progress':
      case 'in progress':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'open':
        return 'bg-indigo-50 text-indigo-700 border-indigo-200';
      case 'clarification':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'mail_away':
      case 'mailaway':
        return 'bg-cyan-50 text-cyan-700 border-cyan-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  // Priority badge styling
  const getPriorityStyle = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'rush':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'urgent':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'high':
        return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'normal':
      default:
        return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };
  
  // Priority emoji indicator
  const getPriorityEmoji = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent': return '🔴';
      case 'rush': return '🔵';
      default: return '🟡';
    }
  };

  // Get field values with fallbacks for both API formats
  const orderId = orderData.order_number || orderData.orderNumber || orderData.id;
  const clientProduct = orderData.client_product || orderData.clientProduct || 'N/A';
  const productId = orderData.product_id || orderData.productId || orderData.external_task_id || orderData.externalTaskId || orderData.task_id || orderData.taskId || orderData.external_loan_id || orderData.externalLoanId || 'N/A';

  // May 1, 2026 — Loan ID surface for Search products.
  // - Source: bulk-upload "Loan ID" column → persisted as
  //   `external_loan_id` (preferred) / `loan_id` (legacy) on the order.
  // - Treat literal CSV placeholders ("NA", "N/A", "-", "", "null") as
  //   "no value" — same rule the bulk loader uses for product_id.
  // - Only displayed when:
  //     (a) order is a Title Search product (isSearchProduct),
  //     (b) a meaningful Loan ID exists, AND
  //     (c) it differs from `productId` (avoids duplicate rendering when
  //         the bulk upload fell back to loan_id for product_id).
  const _rawLoanId = orderData.external_loan_id || orderData.externalLoanId
                  || orderData.loan_id || orderData.loanId || '';
  const _loanIdNorm = String(_rawLoanId || '').trim();
  const _loanIdIsPlaceholder = ['', 'na', 'n/a', '-', 'null', 'none'].includes(_loanIdNorm.toLowerCase());
  const loanId = _loanIdIsPlaceholder ? '' : _loanIdNorm;
  // Feb 2026 — Pass the ORDER OBJECT (not the display-name string) so the
  // canonical `workflow_type === 'TITLE_SEARCH'` classification wins over
  // fragile display-name matching. Previously this passed `clientProduct`
  // (e.g. "AVR"), and the name-only fallback whitelist only knew about
  // "AVR Search" — so when a contract mapping normalized the display name
  // to plain "AVR" (or "MCRRC-AVR" / "NSMRC-AVR"), the Loan ID field
  // silently disappeared from the modal. Source of truth is the catalog's
  // workflow_type, which the API already includes on every order row.
  const showLoanId = isSearchProduct(orderData)
                  && Boolean(loanId)
                  && loanId !== productId;

  // RL-HIDE-TAX-001 (Sep 2026): Research Links card visibility in Order Summary.
  const _rlRole = currentUser?.role;
  const showResearchLinks = Boolean(orderData?.property_county)
    && !RESEARCH_LINKS_HIDDEN_ROLES.includes(_rlRole)
    && (_rlRole !== 'admin' || isSearchProduct(orderData));

  // Feb 26, 2026 — Loan Amount surface (all products). Source = bulk-upload
  // "Loan Amount" column, persisted as `loan_amount` (float) on the order.
  // Hidden entirely when the value is missing/blank to avoid clutter on
  // legacy/manual orders. `0` is treated as a valid stored value.
  const _rawLoanAmount = orderData.loan_amount ?? orderData.loanAmount;
  const _loanAmountNum = (() => {
    if (_rawLoanAmount === null || _rawLoanAmount === undefined || _rawLoanAmount === '') return null;
    const n = typeof _rawLoanAmount === 'string'
      ? parseFloat(_rawLoanAmount.replace(/[^0-9.-]/g, ''))
      : Number(_rawLoanAmount);
    return Number.isFinite(n) ? n : null;
  })();
  const loanAmountDisplay = _loanAmountNum === null
    ? ''
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(_loanAmountNum);
  const showLoanAmount = Boolean(loanAmountDisplay);
  const clientCode = orderData.client_code || orderData.clientCode || 'N/A';
  const currentPriority = priority || orderData.priority || 'normal';  // Use state priority
  const status = orderData.status || 'N/A';
  
  // Property info — formatters return em-dash for missing values, so we
  // also keep a "raw truthy" version to drive the comma-join logic that
  // wants to SKIP missing fields rather than show "—".
  const _rawCity  = orderData.property_city || orderData.propertyCity || orderData.city || '';
  const _rawState = orderData.property_state || orderData.propertyState || orderData.state || '';
  const propertyAddress = formatStreetAddress(orderData.property_address_full || orderData.property_address || orderData.propertyAddress || orderData.fullAddress);
  const propertyCity  = _rawCity  ? formatCity(_rawCity)   : '';
  const propertyState = _rawState ? formatState(_rawState) : '';
  const propertyZip = orderData.property_zip_code || orderData.property_zip || orderData.propertyZip || orderData.zipCode || orderData.zip || '';
  const county = formatCounty(orderData.property_county || orderData.county || orderData.propertyCounty);
  const parcel = orderData.parcel || orderData.parcel_apn || orderData.parcelApn || orderData.certificate_data?.parcel || '';
  
  // Customer info
  const customerName = orderData.customer_name || orderData.customerName || orderData.customer || '';
  const customerEmail = orderData.customer_email || orderData.customerEmail || orderData.email || '';
  const customerPhone = orderData.customer_phone || orderData.customerPhone || orderData.phone || '';
  // Feb 2026 — UI-side defensive normalization. Stored value is
  // preserved on disk; we render the canonical title-case form to
  // match the Tax Cert PDF (pdf_service.py:_normalize_borrower_name)
  // and the CreateOrderForm live-preview. Idempotent for already-
  // clean values; passes through "N/A" placeholder unchanged.
  const _rawBorrower = orderData.borrower_name || orderData.borrowerName || orderData.borrower || '';
  const borrowerName = _rawBorrower ? (normalizeBorrowerName(_rawBorrower) || _rawBorrower) : 'N/A';
  const _rawSeller = (orderData.seller_name || '').trim();  // SELLER-NAME-001
  const sellerName = _rawSeller ? (normalizeBorrowerName(_rawSeller) || _rawSeller) : '';
  
  // Order details
  const orderedDate = orderData.created_at || orderData.createdAt || orderData.created_date || orderData.orderedOn;
  // Loan/Transfer Date — strict binding to the `loan_date` field that bulk
  // upload populates from the CSV "Loan Date" column. Per product spec we
  // MUST NOT fall back to due_date / created_at / ordered_at for this field;
  // if the order has no loan_date (e.g. manual order without one), display
  // nothing. This prevents any derived milestone date from ever rendering
  // here and keeps CSV input = Order Summary display for bulk orders.
  const loanTransferDate = orderData.loan_date || orderData.loanDate;

  // loan_date is a calendar date (no time/TZ), so we format it WITHOUT the
  // ET timezone shift used by formatDateShortET — otherwise "2026-04-16"
  // (stored as midnight UTC) would render as "Apr 15, 2026" in ET because
  // midnight UTC is 8pm ET the previous day. We parse the YYYY-MM-DD head of
  // the ISO string directly.
  const formatLoanDate = (raw) => {
    if (!raw) return 'N/A';
    try {
      const iso = typeof raw === 'string' ? raw : (raw?.toISOString?.() || String(raw));
      const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return 'N/A';
      const [, y, mo, d] = m;
      return formatDateOnly(`${y}-${mo}-${d}`);
    } catch {
      return 'N/A';
    }
  };
  const assigneeName = orderData.assignee_name || orderData.assigneeName || orderData.assignee || 'Unassigned';
  const notes = orderData.notes || orderData.order_notes || orderData.orderNotes || '';

  return (
    <div className="fixed inset-0 z-50" data-testid="order-summary-popup">
      {/* Background overlay — stronger dim, light blur */}
      <div
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-[2px] animate-in fade-in-0 duration-200"
        onClick={onClose}
      />

      {/* Centering wrapper */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        {/* Modal panel — ORDER-SUMMARY-PREMIUM-001 */}
        <div
          className="relative bg-slate-50 text-left overflow-hidden w-[74vw] max-w-[1140px] min-w-[320px] flex flex-col rounded-3xl border border-white/60 animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2 duration-200"
          style={{ boxShadow: '0 30px 80px -20px rgba(15, 23, 42, 0.45)', maxHeight: '92vh' }}
        >
          {/* Header — premium blue with subtle glow */}
          <div className="relative px-6 py-4 overflow-hidden min-h-[104px]" style={{ background: 'linear-gradient(100deg, #1d4ed8 0%, #2563eb 55%, #3b82f6 100%)' }}>
            <div aria-hidden="true" className="pointer-events-none absolute -right-10 -top-16 h-56 w-56 rounded-full bg-white/15 blur-3xl" />
            <div aria-hidden="true" className="pointer-events-none absolute right-28 -bottom-20 h-40 w-40 rounded-full bg-indigo-300/30 blur-3xl" />
            {/* Decorative clipboard / check artwork (right, blended into the gradient) */}
            <img
              src="/assets/order-summary-clipboard.png"
              alt=""
              aria-hidden="true"
              draggable="false"
              className="pointer-events-none select-none absolute right-24 -top-3 h-[150px] w-auto opacity-90 hidden md:block"
              style={{ WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 55%, rgba(0,0,0,0) 100%)', maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 55%, rgba(0,0,0,0) 100%)', filter: 'drop-shadow(0 10px 18px rgba(15,23,42,0.25))' }}
              data-testid="order-summary-header-art"
            />
            <div className="relative flex items-center justify-between gap-4">
              <div className="flex items-center gap-3.5 min-w-0">
                <span className="h-12 w-12 rounded-2xl bg-white text-[#2563eb] grid place-items-center shadow-lg shadow-blue-900/20 shrink-0"><FileText className="w-6 h-6" aria-hidden="true" /></span>
                <div className="min-w-0">
                  <h3 className="text-white font-bold tracking-tight leading-tight" style={{ fontSize: '24px' }}>Order Summary</h3>
                  <p className="text-blue-100 text-[14px] mt-0.5">Complete overview of this order and recent activity.</p>
                </div>
              </div>
              <button
                onClick={onClose}
                aria-label="Close"
                className={`h-10 w-10 rounded-xl bg-white/15 text-white grid place-items-center ${OS_T} hover:bg-white/25 hover:-translate-y-px shrink-0`}
                data-testid="order-summary-close-x"
              >
                <XIcon className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Scrollable content area */}
          <div className="overflow-y-auto flex-1" style={{ minHeight: 0 }}>
          {/* Body — premium card grid */}
          <div className="px-5 pt-3.5 pb-2">
            <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-3">
              {/* LEFT COLUMN */}
              <div className="space-y-2.5">
                {/* Order identity */}
                <SCard testId="order-summary-identity-card">
                  <div className="flex items-center gap-4 flex-wrap">
                    <SIcon icon={FileText} tone="blue" />
                    <div className="min-w-0 flex-1">
                      <SLabel>Order ID</SLabel>
                      <div className="flex items-center gap-2 flex-wrap">
                        <SValue size="lg" className="break-all">{orderId}</SValue>
                        <CopyIconButton value={orderId} label="Order ID" size="sm" />
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border font-semibold uppercase tracking-wide ${getPriorityStyle(currentPriority)}`}
                      style={{ fontSize: '11px' }}
                      data-testid="order-summary-priority-chip"
                    >
                      {getPriorityEmoji(currentPriority)} {currentPriority || 'normal'}
                    </span>
                  </div>
                  {showLoanAmount && (
                    <div className="mt-2 pt-2 border-t border-slate-100" data-testid="order-summary-loan-amount">
                      <SLabel>Loan Amount</SLabel>
                      <div className="flex items-center gap-1.5">
                        <SValue data-testid="order-summary-loan-amount-value">{loanAmountDisplay}</SValue>
                        <CopyIconButton value={loanAmountDisplay} label="Loan Amount" size="sm" />
                      </div>
                    </div>
                  )}
                </SCard>

                {/* Client / Product */}
                <SCard testId="order-summary-client-card">
                  <div className={`grid gap-4 ${showLoanId ? 'sm:grid-cols-2 xl:grid-cols-4' : 'sm:grid-cols-[1.2fr_1.2fr_0.9fr]'} grid-cols-1`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <SIcon icon={Building2} tone="violet" />
                      <div className="min-w-0">
                        <SLabel>Client Code</SLabel>
                        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                          <SValue>{clientCode}</SValue>
                          <CopyIconButton value={clientCode} label="Client Code" size="sm" />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 min-w-0">
                      <SIcon icon={Briefcase} tone="slate" />
                      <div className="min-w-0" title={productDisplayText(clientProduct, orderData.contract_code || orderData.contractCode)}>
                        <SLabel>Client Product</SLabel>
                        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                          <SValue>{clientProduct}</SValue>
                          <ContractCodeChip code={orderData.contract_code || orderData.contractCode} testId="popup-contract-code" size="xs" />
                          <CopyIconButton value={productDisplayText(clientProduct, orderData.contract_code || orderData.contractCode)} label="Client Product" size="sm" />
                        </div>
                      </div>
                    </div>
                    <div className="min-w-0">
                      <SLabel>Product ID</SLabel>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <SValue className="truncate">{productId}</SValue>
                        {productId && productId !== 'N/A' && <CopyIconButton value={productId} label="Product ID" size="sm" />}
                      </div>
                    </div>
                    {showLoanId && (
                      <div className="min-w-0" data-testid="order-summary-loan-id">
                        <SLabel>Loan ID</SLabel>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <SValue className="truncate">{loanId}</SValue>
                          <CopyIconButton value={loanId} label="Loan ID" size="sm" />
                        </div>
                      </div>
                    )}
                  </div>
                </SCard>

                {/* Property */}
                <SCard testId="order-summary-property-card">
                  <div className="flex items-start gap-3">
                    <SIcon icon={MapPin} tone="emerald" />
                    <div className="min-w-0 flex-1">
                      <SLabel>Property Address</SLabel>
                      <div className="flex items-start gap-1.5">
                        <SValue>
                          {[propertyAddress, ...[propertyCity, propertyState, propertyZip].filter(Boolean)].join(', ')}
                        </SValue>
                        <CopyIconButton value={`${propertyAddress}, ${[propertyCity, propertyState, propertyZip].filter(Boolean).join(', ')}`} label="Full Address" size="sm" />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-2 pt-2 border-t border-slate-100 pl-[52px]">
                    <div>
                      <SLabel>County</SLabel>
                      <div className="flex items-center gap-1.5">
                        <SValue>{county}</SValue>
                        <CopyIconButton value={county} label="County" size="sm" />
                      </div>
                    </div>
                    <div>
                      <SLabel>State</SLabel>
                      <div className="flex items-center gap-1.5">
                        <SValue>{propertyState || 'N/A'}</SValue>
                        {propertyState && <CopyIconButton value={propertyState} label="State" size="sm" />}
                      </div>
                    </div>
                  </div>
                </SCard>

                {/* Borrower / Owner */}
                <SCard testId="order-summary-borrower-card">
                  <div className="flex items-center gap-3">
                    <SIcon icon={UserRound} tone="blue" />
                    <div className="min-w-0 flex-1">
                      <SLabel>Borrower / Owner</SLabel>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <SValue>{borrowerName}</SValue>
                        <CopyIconButton value={borrowerName} label="Borrower Name" size="sm" />
                      </div>
                    </div>
                  </div>
                  {sellerName && (
                    <div className="flex items-center gap-3 mt-2 pt-2 border-t border-slate-100" data-testid="popup-seller-row">
                      <SIcon icon={UserRound} tone="slate" />
                      <div className="min-w-0 flex-1">
                        <SLabel>Seller (Current Owner)</SLabel>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <SValue data-testid="popup-seller-name">{sellerName}</SValue>
                          <CopyIconButton value={sellerName} label="Seller Name" size="sm" />
                        </div>
                      </div>
                    </div>
                  )}
                </SCard>
              </div>

              {/* RIGHT COLUMN */}
              <div className="space-y-2.5">
                {/* Status + Assignee */}
                <SCard testId="order-summary-status-card">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <SIcon icon={Box} tone="emerald" />
                      <div className="min-w-0">
                        <SLabel>Status</SLabel>
                        <span className={`inline-flex items-center h-7 px-3 rounded-full border font-semibold capitalize ${getStatusStyle(status)}`} style={{ fontSize: '12px' }} data-testid="order-summary-status-chip">
                          {status?.replace('_', ' ') || 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 min-w-0">
                      <SIcon icon={UserRound} tone="blue" />
                      <div className="min-w-0">
                        <SLabel>Assignee</SLabel>
                        <SValue className={assigneeName === 'Unassigned' ? 'text-slate-400 font-medium' : ''} data-testid="order-summary-assignee">{assigneeName}</SValue>
                      </div>
                    </div>
                  </div>
                </SCard>

                {/* Dates */}
                <SCard testId="order-summary-dates-card">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <SIcon icon={CalendarDays} tone="rose" />
                      <div className="min-w-0">
                        <SLabel>Ordered Date</SLabel>
                        <SValue>{formatDate(orderedDate)}</SValue>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 min-w-0">
                      <SIcon icon={CalendarClock} tone="amber" />
                      <div className="min-w-0">
                        <SLabel>Loan / Transfer Date</SLabel>
                        <SValue data-testid="order-summary-loan-transfer-date">{formatLoanDate(loanTransferDate)}</SValue>
                      </div>
                    </div>
                  </div>
                </SCard>

                {/* Turnaround — same calculations, metric cards */}
                <SCard testId="order-summary-turnaround-card">
                  <div className="flex items-center gap-3 mb-2">
                    <SIcon icon={Clock} tone="violet" />
                    <SLabel>Turnaround</SLabel>
                  </div>
                  <div className={`grid gap-3 ${[daysElapsed !== null, daysRemaining !== null, !!daysLateInfo].filter(Boolean).length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                    {daysElapsed !== null && (
                      <div className={`rounded-2xl border border-blue-100 bg-gradient-to-b from-blue-50/80 to-slate-50 px-3 py-2.5 text-center ${OS_T} hover:shadow-sm`} data-testid="order-summary-elapsed">
                        <span className="block text-[12.5px] font-medium text-slate-600">Elapsed</span>
                        <span className="block text-[24px] font-bold text-slate-900 leading-tight mt-0.5">{daysElapsed}d</span>
                      </div>
                    )}
                    {daysRemaining !== null && (
                      <div className={`rounded-2xl border px-3 py-2.5 text-center ${OS_T} hover:shadow-sm ${daysRemaining < 0 ? 'bg-rose-50 border-rose-200' : daysRemaining <= 2 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`} data-testid="order-summary-remaining">
                        <span className="block text-[12.5px] font-medium text-slate-600">Remaining</span>
                        <span className={`block text-[24px] font-bold leading-tight mt-0.5 ${daysRemaining < 0 ? 'text-rose-600' : daysRemaining <= 2 ? 'text-amber-600' : 'text-slate-900'}`}>
                          {daysRemaining < 0 ? `${Math.abs(daysRemaining)}d late` : `${daysRemaining}d`}
                        </span>
                      </div>
                    )}
                    {daysLateInfo && (
                      <div className={`rounded-2xl border px-3 py-2.5 text-center ${OS_T} hover:shadow-sm ${daysLateInfo.colorClass}`} data-testid="order-summary-days-late">
                        <span className="block text-[12.5px] font-medium opacity-80">Days Late</span>
                        <span className="block text-[24px] font-bold leading-tight mt-0.5">{daysLateInfo.displayText}</span>
                      </div>
                    )}
                  </div>
                </SCard>

                {/* Parcel (if available) */}
                {parcel && (
                  <SCard testId="order-summary-parcel-card">
                    <SLabel>Parcel #</SLabel>
                    <div className="flex items-center gap-1.5">
                      <SValue>{parcel}</SValue>
                      <CopyIconButton value={parcel} label="Parcel ID" size="sm" />
                    </div>
                  </SCard>
                )}
              </div>
            </div>

            {/* Conditional sections - Mail-Away, Clarification, Notes (collapsed by default) */}
            {/* Research Links (RESEARCH-LINKS-001) — condensed, collapsed by default.
                RL-HIDE-TAX-001: hidden for tax roles on all orders; admin sees it on Title Search orders only.
                Not rendering the card also skips its GET /research-links call. */}
            {showResearchLinks && (
              <div className="mt-3" data-testid="order-summary-research-links">
                <ResearchLinksCard orderId={orderData.id} compact readOnly />
              </div>
            )}
            {/* MAILAWAY-RECOVERY-001: active request on file while the order is not in Mail-Away */}
            <MailAwayRecoveryCard
              order={orderData}
              currentUser={currentUser}
              compact
              onReturned={async () => {
                // optimistic: banner disappears + Mail-Away Requests section mounts immediately
                setHydratedOrder((prev) => ({ ...(prev || orderDataProp), status: 'mail_away' }));
                await rehydrate();
                setActivityRefreshKey((k) => k + 1);
                if (onUpdate) onUpdate({ ...orderData, status: 'mail_away' });
              }}
            />
            {/* Mail-Away Status Block — Tax engine when the order is in Mail-Away; MLS component rows
                (MLS-MAILAWAY-ENTITY-001) always show when they exist, in the same section, MLS endpoints only. */}
            {(() => {
              const taxMailAway = status === 'mail_away' || status === 'mailaway' || status === 'mail_away_received';
              const mlsRows = (orderData.mail_away_requests || []).filter(isMlsMailAwayRow);
              if (!taxMailAway && mlsRows.length === 0) return null;
              const mlsCanAct = ['admin', 'tax_search_team_leader', 'team_leader', 'processor'].includes(currentUser?.role);
              const onMlsChanged = async () => { await rehydrate(); setActivityRefreshKey((k) => k + 1); if (onUpdate) onUpdate(); };
              const mlsBlock = <MlsMailAwaySummaryRows orderId={orderData.id} requests={mlsRows} canAct={mlsCanAct} standalone={!taxMailAway} onChanged={onMlsChanged} />;
              return (
                <div className="mt-3 pt-3 border-t border-gray-200" data-testid="order-summary-mailaway-section">
                  {taxMailAway ? (
                    <MultiJurisdictionMailAway
                      orderId={orderData.id}
                      orderData={orderData}
                      currentUser={currentUser}
                      onUpdate={() => { if (onUpdate) onUpdate(); }}
                      extraRows={mlsRows.length ? <div className="space-y-2 mb-3">{mlsBlock}</div> : null}
                      extraSummary={mlsRows.length ? mlsMailAwaySummary(mlsRows) : null}
                    />
                  ) : mlsBlock}
                </div>
              );
            })()}
            
            {/* Clarification Status Block */}
            {status === 'clarification' && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <ClarificationStatusBlock
                  orderData={orderData}
                  currentUser={currentUser}
                  onUpdate={() => {
                    onClose();
                    if (onUpdate) onUpdate();
                  }}
                />
              </div>
            )}

            {/* MLS-CLARIFICATION-CYCLE-001: component-level MLS clarification (dual orders) — same block, component scope */}
            {orderData?.components?.mls?.status === 'clarification' && (
              <div className="mt-3 pt-3 border-t border-gray-200" data-testid="order-summary-mls-clarification-section">
                <ClarificationStatusBlock
                  orderData={orderData}
                  currentUser={currentUser}
                  component="mls"
                  onUpdate={async () => { await rehydrate(); setActivityRefreshKey((k) => k + 1); if (onUpdate) onUpdate(); }}
                />
              </div>
            )}
            
            {/* TYPING-REVIEW-001 — Order Summary is informational only; all
                Typing workflow actions live on the Fulfillment page */}
            <TypingOrderSection
              orderData={orderData}
              currentUser={currentUser}
              onUpdate={onUpdate}
              onClose={onClose}
              showActions={false}
            />

            {/* Fax Delivery Tracking */}
            {(orderData?.fax_deliveries?.length > 0 || orderData?.fax_delivery) && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <FaxDeliverySection
                  orderData={orderData}
                  currentUser={currentUser}
                  onUpdate={() => {
                    if (onUpdate) onUpdate();
                  }}
                />
              </div>
            )}
            {/* TAX-PENDING-EMAIL-001 — outbound Tax Collector email requests */}
            {orderData?.tax_email_requests?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <TaxEmailRequestsSection orderData={orderData} currentUser={currentUser} onUpdate={() => { if (onUpdate) onUpdate(); }} />
              </div>
            )}
            
            {/* Notes (if available, collapsible) */}
            {notes && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <details className="group">
                  <summary className="cursor-pointer text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                    <svg className="w-3 h-3 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Notes
                  </summary>
                  <div 
                    className="mt-2 text-gray-700 text-sm bg-gray-50 rounded-lg p-3 max-h-20 overflow-y-auto"
                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {notes}
                  </div>
                </details>
              </div>
            )}
          </div>
          
          {/* Quick Priority Update Panel - Compact (For Admin/Team Leader Only) */}
          {canEditPriority && (
            <div className="border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white px-5 py-2.5">
              {/* Quick Action Buttons - Always Visible */}
              {!showPriorityPanel ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-700">⚡ Priority:</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${
                      priority === 'urgent' ? 'bg-red-100 text-red-700 border-red-300' :
                      priority === 'rush' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                      'bg-gray-100 text-gray-600 border-gray-300'
                    }`}>
                      {priority === 'urgent' ? '🔴' : priority === 'rush' ? '🔵' : '🟡'} {(priority || 'normal').toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {priority !== 'rush' && (
                      <button
                        onClick={handleMarkAsRush}
                        className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-300 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors flex items-center gap-1"
                      >
                        🔵 Rush
                      </button>
                    )}
                    {priority !== 'urgent' && (
                      <button
                        onClick={handleMarkAsUrgent}
                        className="px-2.5 py-1 bg-red-50 text-red-700 border border-red-300 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors flex items-center gap-1"
                      >
                        🔴 Urgent
                      </button>
                    )}
                    {priority !== 'normal' && (
                      <button
                        onClick={() => {
                          setPriority('normal');
                          setPriorityNotes('');
                          handleQuickPrioritySave('normal');
                        }}
                        className="px-2.5 py-1 bg-gray-50 text-gray-600 border border-gray-300 rounded-lg text-xs font-medium hover:bg-gray-100 transition-colors"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                /* Expanded Priority Panel */
                <div className={`rounded-xl p-4 border-2 ${
                  priority === 'urgent' ? 'border-red-300 bg-red-50/50' :
                  priority === 'rush' ? 'border-blue-300 bg-blue-50/50' :
                  'border-gray-200 bg-white'
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                      🔥 Quick Priority Update
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                        priority === 'urgent' ? 'bg-red-100 text-red-700' :
                        priority === 'rush' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {(priority || 'normal').toUpperCase()}
                      </span>
                    </h4>
                    <button
                      onClick={() => {
                        setShowPriorityPanel(false);
                        setPriority(orderData?.priority || 'normal');
                        setPriorityNotes(orderData?.priority_notes || '');
                        setPriorityError('');
                      }}
                      className="text-gray-400 hover:text-gray-600 text-lg"
                    >
                      ✕
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    {/* Priority Level */}
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">
                        Priority Level <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={priority}
                        onChange={(e) => {
                          setPriority(e.target.value);
                          setPriorityError('');
                        }}
                        className={`w-full px-3 py-2 border rounded-lg text-sm font-medium ${
                          priority === 'urgent' ? 'border-red-300 bg-red-50 text-red-700' :
                          priority === 'rush' ? 'border-blue-300 bg-blue-50 text-blue-700' :
                          'border-gray-300 bg-white text-gray-700'
                        }`}
                      >
                        <option value="normal">🟡 Normal</option>
                        <option value="rush">🔵 Rush</option>
                        <option value="urgent">🔴 Urgent</option>
                      </select>
                    </div>
                    
                    {/* Justification */}
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">
                        Justification {(priority === 'rush' || priority === 'urgent') && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type="text"
                        value={priorityNotes}
                        onChange={(e) => {
                          setPriorityNotes(e.target.value);
                          setPriorityError('');
                        }}
                        placeholder={priority === 'normal' ? 'Optional...' : 'Required: Why is this urgent?'}
                        className={`w-full px-3 py-2 border rounded-lg text-sm ${
                          priorityError ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}
                      />
                    </div>
                  </div>
                  
                  {priorityError && (
                    <p className="text-red-600 text-xs mb-2">⚠️ {priorityError}</p>
                  )}
                  
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => {
                        setShowPriorityPanel(false);
                        setPriority(orderData?.priority || 'normal');
                        setPriorityNotes(orderData?.priority_notes || '');
                        setPriorityError('');
                      }}
                      className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleQuickPrioritySave()}
                      disabled={isSavingPriority}
                      className={`px-5 py-2 rounded-lg text-sm font-semibold text-white transition-all shadow-sm hover:shadow-md ${
                        priority === 'urgent' ? 'bg-red-600 hover:bg-red-700' :
                        priority === 'rush' ? 'bg-blue-600 hover:bg-blue-700' :
                        'bg-gray-600 hover:bg-gray-700'
                      } ${isSavingPriority ? 'opacity-75 cursor-not-allowed' : ''}`}
                    >
                      {isSavingPriority ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </span>
                      ) : (
                        '✓ Save Priority'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Recent Activity Section */}
          <div className="px-5 pb-3">
            <RecentActivitySection orderId={orderData.id} refreshKey={activityRefreshKey}
              onViewAll={() => { onClose?.(); navigate(`/orders/${orderData.id}/activity`); }} />
          </div>
          </div>{/* End scrollable content area */}
          
          {/* Footer - Sticky, always visible */}
          <div
            className="bg-white px-5 py-3 flex items-center justify-between gap-3 border-t border-slate-200"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={onClose}
                className={`h-10 px-4 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white text-slate-700 font-semibold text-[14px] shadow-sm ${OS_T} hover:bg-slate-50 hover:-translate-y-px hover:shadow`}
                data-testid="order-summary-close-btn"
              >
                <XIcon className="w-4 h-4" aria-hidden="true" /> Close
              </button>
              
              {/* Request Edit Button - Order Desk only, for assigned orders */}
              {showRequestEditButton && onRequestEdit && (
                <button
                  onClick={onRequestEdit}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium flex items-center gap-1.5 text-sm"
                  data-testid="request-edit-btn"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Request Edit
                </button>
              )}

              {/* Feb 2026 — DELIVERY-EDIT-RBAC-001 — Edit Order Button
                  for Admin and Team Leaders. Caller is responsible for
                  the team-ownership pre-check before setting
                  `showEditOrderButton=true`. */}
              {showEditOrderButton && onEditOrder && (
                <button
                  onClick={onEditOrder}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-1.5 text-sm"
                  data-testid="edit-order-btn"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Order
                </button>
              )}
              
              {/* Reopen for Edit Button - Admin/Team Leader only, for completed orders */}
              {canManageOrderStatus && (status?.toLowerCase() === 'complete' || status?.toLowerCase() === 'completed') && (
                <button
                  onClick={() => setShowReopenModal(true)}
                  className="px-4 py-2 border border-orange-300 rounded-lg text-orange-600 hover:bg-orange-50 hover:border-orange-400 transition-colors font-medium flex items-center gap-1.5 text-sm"
                  data-testid="reopen-order-btn"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reopen
                </button>
              )}
              
              {/* Cancel Order Button - Admin/Team Leader only, if order is cancellable */}
              {canManageOrderStatus && isOrderCancellable && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="px-4 py-2 border border-red-300 rounded-lg text-red-600 hover:bg-red-50 hover:border-red-400 transition-colors font-medium text-sm"
                  data-testid="cancel-order-btn"
                >
                  Cancel
                </button>
              )}
              
              {/* Restore Order Button - Admin/Team Leader only, if order is cancelled */}
              {canManageOrderStatus && isOrderRestorable && (
                <button
                  onClick={() => setShowRestoreModal(true)}
                  className="px-4 py-2 border border-emerald-300 rounded-lg text-emerald-600 hover:bg-emerald-50 hover:border-emerald-400 transition-colors font-medium text-sm"
                  data-testid="restore-order-btn"
                >
                  Restore
                </button>
              )}
              
              {/* Reassign Button - Admin/Team Leader only. For terminal orders the
                  dialog itself shows the "cannot be reassigned" explanation. */}
              {canManageOrderStatus && (
                <button
                  onClick={() => setShowReassignDialog(true)}
                  className={`px-4 py-2 border rounded-lg transition-colors font-medium flex items-center gap-1.5 text-sm ${
                    ['complete', 'completed', 'cancelled'].includes(orderStatus)
                      ? 'border-slate-200 text-slate-400 hover:bg-slate-50 cursor-not-allowed'
                      : 'border-blue-300 text-blue-600 hover:bg-blue-50 hover:border-blue-400'
                  }`}
                  title={['complete', 'completed', 'cancelled'].includes(orderStatus)
                    ? 'Completed order cannot be reassigned' : undefined}
                  data-testid="reassign-order-popup-btn"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  Reassign
                </button>
              )}
            </div>
            
            {/* Online Tax Callback Sheet / Title Research Log - Hidden for Order Desk (processor-level function) */}
            {canAccessFulfillment && (
              <button
                onClick={handleOpenFulfillment}
                className={`h-10 px-4 inline-flex items-center gap-2 rounded-xl bg-[#2563eb] text-white font-semibold text-[14px] shadow-md shadow-blue-600/25 ${OS_T} hover:bg-[#1d4ed8] hover:-translate-y-px hover:shadow-lg`}
                data-testid={isTaxProduct(orderData) ? "online-tax-callback-btn" : "title-research-log-btn"}
              >
                <FileText className="w-4 h-4" aria-hidden="true" />
                {isTaxProduct(orderData) ? getCallbackSheetLabel(orderData) : 'Research Log'}
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Cancel Order Confirmation Modal */}
      {showCancelModal && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-red-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Cancel Order</h3>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="px-6 py-5">
              <p className="text-gray-700 mb-2">
                Are you sure you want to cancel this order?
              </p>
              <p className="text-sm text-gray-500 mb-4">
                <strong className="text-gray-700">Order:</strong> {orderData.order_number || orderData.orderNumber}
              </p>
              
              {/* Reason Input */}
              <div className="mb-4">
                <label htmlFor="cancelReason" className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Cancellation <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="cancelReason"
                  value={cancelReason}
                  onChange={(e) => {
                    setCancelReason(e.target.value);
                    if (cancelError) setCancelError('');
                  }}
                  placeholder="Enter reason for cancellation (minimum 10 characters)"
                  className={`w-full px-3 py-2 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                    cancelError ? 'border-red-400 bg-red-50' : 'border-gray-300'
                  }`}
                  rows={3}
                  data-testid="cancel-reason-input"
                />
                {cancelError && (
                  <p className="mt-1 text-sm text-red-600">{cancelError}</p>
                )}
                <p className="mt-1 text-xs text-gray-400">
                  {cancelReason.trim().length}/10 characters minimum
                </p>
              </div>
              
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span>This action cannot be undone. The order will be moved to Cancelled status.</span>
                </p>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={handleCloseCancelModal}
                disabled={isCancelling}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-white transition-colors font-medium disabled:opacity-50"
                data-testid="cancel-modal-dismiss-btn"
              >
                Go Back
              </button>
              <button
                onClick={handleCancelOrder}
                disabled={isCancelling || cancelReason.trim().length < 10}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="confirm-cancel-order-btn"
              >
                {isCancelling ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Cancelling...
                  </>
                ) : (
                  'Confirm Cancel'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Restore Order Modal — ORDER-RESTORE-STATE-001 */}
      {showRestoreModal && (
        <RestoreOrderModal order={orderData} onClose={() => setShowRestoreModal(false)} onRestored={handleRestored} />
      )}

      {/* Reopen for Edit Confirmation Modal */}
      {showReopenModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
          onClick={() => setShowReopenModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="reopen-order-modal"
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-orange-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Reopen Order for Editing</h3>
                  <p className="text-sm text-gray-500">{orderData?.order_number || orderData?.orderNumber || 'N/A'}</p>
                </div>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Warning Banner */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-sm text-amber-800">
                    This order was completed and may have been delivered to the client.
                  </p>
                </div>
              </div>

              {/* What will happen */}
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-2">Reopening this order will:</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                    Set status back to <strong>Open</strong>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                    <strong>Unassign</strong> the order from current processor
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                    <strong>Unmark</strong> any Final PDF files
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                    Reset delivery status to <strong>Pending</strong>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                    Require <strong>re-finalization</strong> before completing again
                  </li>
                </ul>
              </div>
              
              {/* Reason Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Reopening <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.target.value)}
                  placeholder="Explain why this order needs to be reopened (e.g., client requested changes, data correction needed, etc.)"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                  data-testid="reopen-reason-input"
                />
                {reopenError && (
                  <p className="mt-1 text-sm text-red-600">{reopenError}</p>
                )}
                <p className="mt-1 text-xs text-gray-400">
                  {reopenReason.trim().length}/10 characters minimum {reopenReason.trim().length >= 10 ? '✓' : ''}
                </p>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowReopenModal(false);
                  setReopenReason('');
                  setReopenError('');
                }}
                disabled={isReopening}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-white transition-colors font-medium disabled:opacity-50"
                data-testid="reopen-modal-dismiss-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleReopenOrder}
                disabled={isReopening || reopenReason.trim().length < 10}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="confirm-reopen-order-btn"
              >
                {isReopening ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Reopening...
                  </>
                ) : (
                  'Confirm Reopen'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Reassign Order Dialog */}
      {canManageOrderStatus && (
        <OrderReassignmentDialog
          isOpen={showReassignDialog}
          onClose={() => setShowReassignDialog(false)}
          order={orderData}
          currentUserId={currentUser?.id}
          currentUserRole={currentUser?.role}
          onSuccess={(result) => {
            // Notify parent to refresh data
            if (onUpdate && typeof onUpdate === 'function') {
              onUpdate();
            }
            toast.success(`Order reassigned to ${result.assignee_name || 'new processor'}`);
          }}
        />
      )}
    </div>
  );
};

export default OrderSummaryPopup;
